import { z } from "zod";

export const operationSchema = z.enum([
  "merge",
  "split",
  "compress",
  "pdf_to_docx",
  "pdf_to_images",
  "convert",
  "ocr"
]);

export type DocumentOperation = z.infer<typeof operationSchema>;
export const executionModeSchema = z.enum(["auto", "local", "server"]);
export type ExecutionMode = z.infer<typeof executionModeSchema>;
export type OcrLanguage = "zh-CN" | "zh-TW" | "en" | "ja";

const base = {
  inputAssetIds: z.array(z.string().min(1)).max(100),
  execution: executionModeSchema.default("auto"),
  idempotencyKey: z.uuid()
};

const mergeRequest = z.object({
  operation: z.literal("merge"),
  inputAssetIds: base.inputAssetIds.min(2, "Merge requires at least two PDF files."),
  parameters: z.object({}).passthrough().default({}),
  execution: base.execution,
  idempotencyKey: base.idempotencyKey
});

const splitRequest = z.object({
  operation: z.literal("split"),
  inputAssetIds: base.inputAssetIds.length(1),
  parameters: z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("ranges"), ranges: z.string().min(1) }),
    z.object({ mode: z.literal("every_n"), every: z.number().int().min(1).max(500) }),
    z.object({ mode: z.literal("selected"), pages: z.string().min(1) })
  ]),
  execution: base.execution,
  idempotencyKey: base.idempotencyKey
});

const compressRequest = z.object({
  operation: z.literal("compress"),
  inputAssetIds: base.inputAssetIds.length(1),
  parameters: z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("lossless") }),
    z.object({
      mode: z.literal("balanced"),
      imageDpi: z.number().int().min(72).max(300).default(144),
      quality: z.number().min(0.1).max(1).default(0.78)
    })
  ]).default({ mode: "lossless" }),
  execution: base.execution,
  idempotencyKey: base.idempotencyKey
});

const pdfToDocxRequest = z.object({
  operation: z.literal("pdf_to_docx"),
  inputAssetIds: base.inputAssetIds.length(1),
  parameters: z.object({
    ocrFallback: z.boolean().default(false),
    language: z.enum(["zh-CN", "zh-TW", "en", "ja"]).optional()
  }).default({ ocrFallback: false }),
  execution: base.execution,
  idempotencyKey: base.idempotencyKey
});

const pdfToImagesRequest = z.object({
  operation: z.literal("pdf_to_images"),
  inputAssetIds: base.inputAssetIds.length(1),
  parameters: z.object({
    format: z.enum(["png", "jpeg"]).default("png"),
    dpi: z.number().int().min(72).max(300).default(144),
    quality: z.number().min(0.1).max(1).optional(),
    pages: z.string().min(1).optional()
  }).default({ format: "png", dpi: 144 }),
  execution: base.execution,
  idempotencyKey: base.idempotencyKey
});

export const conversionTargetSchema = z.enum(["pdf", "text", "markdown", "html", "json", "xml", "csv", "epub", "azw3"]);
export type ConversionTarget = z.infer<typeof conversionTargetSchema>;

const convertRequest = z.object({
  operation: z.literal("convert"),
  inputAssetIds: base.inputAssetIds.min(1),
  parameters: z.object({ target: conversionTargetSchema }),
  execution: base.execution,
  idempotencyKey: base.idempotencyKey
});

const ocrRequest = z.object({
  operation: z.literal("ocr"),
  inputAssetIds: base.inputAssetIds.length(1),
  parameters: z.object({
    mode: z.enum(["missing_text", "all"]).default("missing_text"),
    languages: z.array(z.enum(["zh-CN", "zh-TW", "en", "ja"])).min(1).max(4)
  }),
  execution: base.execution,
  idempotencyKey: base.idempotencyKey
});

export const createJobRequestSchema = z.discriminatedUnion("operation", [
  mergeRequest,
  splitRequest,
  compressRequest,
  pdfToDocxRequest,
  pdfToImagesRequest,
  convertRequest,
  ocrRequest
]);

export type CreateJobRequest = z.infer<typeof createJobRequestSchema>;
